import React, { useEffect, useState } from 'react';

import { Banner } from '@buildoutinc/blueprint-react/ui/Banner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';

import Button from 'shared/button';

export default function AccessChangedBanner() {
  const [addedLevels, setAddedLevels] = useState({});
  const [removedLevels, setRemovedLevels] = useState({});

  const joinLevels = (levels) => {
    return (
      Object.values(levels).map((level, index) => {
        return (
          <React.Fragment key={level}>
            <span className="fw-bold text-capitalize">{level}</span>
            {delineator(Object.keys(levels).length, index)}
          </React.Fragment>
        );
      })
    );
  };

  const delineator = (length, index) => {
    if (index < length - 2) {
      return <span>, </span>;
    } else if (index === length - 2) {
      return <span>, and </span>;
    } else {
      return '';
    }
  };

  const pluralize = (word, count) => {
    if (word === 'has') {
      return count < 2 ? word : 'have';
    }
    if (word === 'have') {
      return count > 1 ? word : 'has';
    }
    return count > 1 ? `${word}s` : word;
  };

  const selectCaAccessSettings = () => {
    document.getElementById('ca-access-settings-tab').click();
  };

  const addedCount = Object.keys(addedLevels).length;
  const removedCount = Object.keys(removedLevels).length;

  const levelAddedTitle = (
    <React.Fragment>
      {joinLevels(addedLevels)} {pluralize('level', addedCount)} {' '}
      {pluralize('has', addedCount)} been added to your CA & Access Settings
    </React.Fragment>
  );
  const levelAddedContent = (
    <React.Fragment>
      View{' '}
      <a style={{ cursor: 'pointer' }} onClick={selectCaAccessSettings}>
      CA & Access Settings</a> to configure how leads view your documents.
    </React.Fragment>
  );

  const levelRemovedTitle = (
    <React.Fragment>
      {joinLevels(removedLevels)} {pluralize('level', removedCount)}{' '}
      {pluralize('has', removedCount)} been removed from your CA & Access Settings
    </React.Fragment>
  );
  const levelRemovedContent = (
    <React.Fragment>
      Please update the{' '}
      <a style={{ cursor: 'pointer' }} onClick={selectCaAccessSettings}>
      CA & Access Settings</a> to configure how leads view your documents.
    </React.Fragment>
  );

  const levelAddedAndRemovedTitle = (
    <React.Fragment>
      Access levels have been changed. Please View{' '}
      <a style={{ cursor: 'pointer' }} onClick={selectCaAccessSettings}>
      CA & Access Settings</a> to configure how leads view your documents.
    </React.Fragment>
  );
  const levelAddedAndRemovedContent = (
    <React.Fragment>
      {joinLevels(addedLevels)} {pluralize('level', addedCount)}{' '}
      {pluralize('have', addedCount)} been Added - View{' '}
      <a style={{ cursor: 'pointer' }} onClick={selectCaAccessSettings}>
      CA & Access Settings</a> to configure how leads view your documents
      <br />
      {joinLevels(removedLevels)} {pluralize('level', removedCount)}{' '}
      {pluralize('has', removedCount)} been removed - Leads that were previously at{' '}
      {pluralize('these', removedCount)} levels will be moved to the lower level
    </React.Fragment>
  );

  const addLevel = (level) => {
    if (removedLevels.hasOwnProperty(level.id)) {
      const updatedRemovedLevels = { ...removedLevels };
      delete updatedRemovedLevels[level.id];
      setRemovedLevels(updatedRemovedLevels);
    } else {
      setAddedLevels((prevAddedLevels) => {
        const updatedAddedLevels = { ...prevAddedLevels };
        updatedAddedLevels[level.id] = level.level;
        return updatedAddedLevels;
      });
    }
  };

  const removeLevel = (level) => {
    if (addedLevels.hasOwnProperty(level.id)) {
      const updatedAddedLevels = { ...addedLevels };
      delete updatedAddedLevels[level.id];
      setAddedLevels(updatedAddedLevels);
    } else {
      setRemovedLevels((prevRemovedLevels) => {
        const updatedRemovedLevels = { ...prevRemovedLevels };
        updatedRemovedLevels[level.id] = level.level;
        return updatedRemovedLevels;
      });
    }
  };

  useEffect(() => {
    const handleAddLevel = event => addLevel(event.detail);
    const handleRemoveLevel = event => removeLevel(event.detail);

    document.addEventListener('accessLevelsAdded', handleAddLevel);
    document.addEventListener('accessLevelsRemoved', handleRemoveLevel);

    return () => {
      document.removeEventListener('accessLevelsAdded', handleAddLevel);
      document.removeEventListener('accessLevelsRemoved', handleRemoveLevel);
    };
  }, [addedCount, removedCount]);

  const contentForDisplay = () => {
    if (addedCount > 0 && removedCount > 0) {
      return levelAddedAndRemovedContent;
    } else if (addedCount > 0) {
      return levelAddedContent;
    } else if (removedCount > 0) {
      return levelRemovedContent;
    }
  };

  const titleForDisplay = () => {
    if (addedCount > 0 && removedCount > 0) {
      return levelAddedAndRemovedTitle;
    } else if (addedCount > 0) {
      return levelAddedTitle;
    } else if (removedCount > 0) {
      return levelRemovedTitle;
    }
  };

  const clearBanner = () => {
    setAddedLevels([]);
    setRemovedLevels([]);
  };

  const show = addedCount > 0 || removedCount > 0;

  if (!show) return null;

  return (
    <Banner className="align-items-center" variant="warning">
      <Banner.Content className="align-items-center">
        <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
        <div className="flex-fill">
          <div><b>{titleForDisplay()}</b></div>
          <div>{contentForDisplay()}</div>
        </div>
      </Banner.Content>
      <Banner.Actions>
        <Button icon={faClose} outlined onClick={clearBanner} />
      </Banner.Actions>
    </Banner>
  );
}
